import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  Link,
} from "@mui/material";
import {
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckIcon,
  SaveOutlined as SaveOutlinedIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useQuery, useQueryClient } from "react-query";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { db } from "../../services/firebase";
import { updateOpinion } from "../../services/api";
import { WeekOption, Topic, WeeklyTopicsWithDetails } from "../../types";
import DifficultyChip from "../common/DifficultyChip";
import GenreChip from "../common/GenreChip";
import Logger from "../../utils/logger";

// === 週ID計算ヘルパー ===

const getISOWeekNumber = (date: Date): { year: number; week: number } => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNumber = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNumber };
};

const generateWeekId = (date: Date): string => {
  const { year, week } = getISOWeekNumber(date);
  return `${year}-W${week.toString().padStart(2, "0")}`;
};

const getWeekStartDate = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekEndDate = (date: Date): Date => {
  const startDate = getWeekStartDate(date);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);
  return endDate;
};

const formatWeekLabel = (startDate: Date, endDate: Date): string => {
  const { year, week } = getISOWeekNumber(startDate);
  const startMonth = startDate.getMonth() + 1;
  const startDay = startDate.getDate();
  const endMonth = endDate.getMonth() + 1;
  const endDay = endDate.getDate();
  return `${year}年 第${week}週 (${startMonth}/${startDay}-${endMonth}/${endDay})`;
};

// === Firestore データ取得 ===

const fetchWeekOptionsFromFirestore = async (
  weeksAhead: number = 4,
  weeksBehind: number = 4
): Promise<{ weeks: WeekOption[] }> => {
  const weeks: WeekOption[] = [];
  const today = new Date();
  const currentWeekStart = getWeekStartDate(today);

  for (let i = weeksBehind; i > 0; i--) {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(currentWeekStart.getDate() - i * 7);
    const weekEnd = getWeekEndDate(weekStart);
    weeks.push({
      weekId: generateWeekId(weekStart),
      weekStartDate: weekStart.toISOString(),
      weekEndDate: weekEnd.toISOString(),
      label: formatWeekLabel(weekStart, weekEnd),
      hasData: false,
    });
  }

  const currentWeekEnd = getWeekEndDate(currentWeekStart);
  weeks.push({
    weekId: generateWeekId(currentWeekStart),
    weekStartDate: currentWeekStart.toISOString(),
    weekEndDate: currentWeekEnd.toISOString(),
    label: formatWeekLabel(currentWeekStart, currentWeekEnd) + "（今週）",
    hasData: false,
  });

  for (let i = 1; i <= weeksAhead; i++) {
    const weekStart = new Date(currentWeekStart);
    weekStart.setDate(currentWeekStart.getDate() + i * 7);
    const weekEnd = getWeekEndDate(weekStart);
    weeks.push({
      weekId: generateWeekId(weekStart),
      weekStartDate: weekStart.toISOString(),
      weekEndDate: weekEnd.toISOString(),
      label: formatWeekLabel(weekStart, weekEnd),
      hasData: false,
    });
  }

  const existingDocs = await Promise.all(
    weeks.map((w) => getDoc(doc(db, "weeklyTopics", w.weekId)))
  );
  existingDocs.forEach((docSnap, index) => {
    weeks[index].hasData = docSnap.exists();
  });

  return { weeks };
};

const fetchWeeklyTopicsByIdFromFirestore = async (
  weekId: string
): Promise<WeeklyTopicsWithDetails> => {
  const docRef = doc(db, "weeklyTopics", weekId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error("Weekly topics not found");
  }

  const data = docSnap.data();
  const topicIds = (data.topicIds || []) as string[];

  const topics: Topic[] = [];
  if (topicIds.length > 0) {
    const topicDocs = await Promise.all(topicIds.map((id) => getDoc(doc(db, "topics", id))));
    for (const topicDoc of topicDocs) {
      if (topicDoc.exists()) {
        const topicData = topicDoc.data();
        topics.push({
          id: topicDoc.id,
          difficulty: topicData.difficulty,
          genre: topicData.genre,
          theme: topicData.theme,
          sourceText: topicData.sourceText,
          background: topicData.background || "",
          opinionPatterns: topicData.opinionPatterns || [],
          isPublished: topicData.isPublished,
          imageUrl: topicData.imageUrl || null,
          xPostUrl: topicData.xPostUrl || "",
        } as Topic);
      }
    }
  }

  const weekStartDate = data.weekStartDate as Timestamp | null;
  const weekEndDate = data.weekEndDate as Timestamp | null;
  const createdAt = data.createdAt as Timestamp | null;
  const updatedAt = data.updatedAt as Timestamp | null;

  return {
    id: docSnap.id,
    weekStartDate: weekStartDate?.toDate?.().toISOString() || "",
    weekEndDate: weekEndDate?.toDate?.().toISOString() || "",
    topicIds,
    topics,
    createdAt: createdAt?.toDate?.().toISOString() || "",
    updatedAt: updatedAt?.toDate?.().toISOString() || "",
    createdBy: data.createdBy || "",
  };
};

// === URL バリデーション ===

const validateXPostUrl = (value: string): string | null => {
  if (!value) return null;
  if (!value.startsWith("https://x.com/") && !value.startsWith("https://twitter.com/")) {
    return "https://x.com/ または https://twitter.com/ で始まるURLを入力してください";
  }
  return null;
};

const XPostUrlSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedWeek, setSelectedWeek] = useState<WeekOption | null>(null);
  // topicId → 入力中のX投稿URL
  const [editedXPostUrls, setEditedXPostUrls] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveResultMessage, setSaveResultMessage] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);

  const {
    data: weekOptionsData,
    isLoading: isLoadingWeeks,
    error: weeksError,
  } = useQuery("xPostUrlWeekOptions", () => fetchWeekOptionsFromFirestore(4, 4), {
    staleTime: 1000 * 60 * 5,
    onSuccess: (data) => {
      // 初回ロード時に今週を自動選択
      if (!selectedWeek) {
        const currentWeek = data.weeks.find((w) => w.label.includes("（今週）"));
        if (currentWeek?.hasData) {
          setSelectedWeek(currentWeek);
        }
      }
    },
  });

  const {
    data: weekDetails,
    isLoading: isLoadingDetails,
    error: detailsError,
  } = useQuery(
    ["xPostUrlWeekDetails", selectedWeek?.weekId],
    () => fetchWeeklyTopicsByIdFromFirestore(selectedWeek!.weekId),
    {
      enabled: !!selectedWeek?.hasData,
      staleTime: 1000 * 60 * 5,
    }
  );

  // 週の詳細を読み込んだら、入力欄を保存済みのURLで初期化する
  useEffect(() => {
    if (weekDetails?.topics) {
      const initialXPostUrls: Record<string, string> = {};
      for (const topic of weekDetails.topics) {
        initialXPostUrls[topic.id!] = topic.xPostUrl || "";
      }
      setEditedXPostUrls(initialXPostUrls);
      setSaveResultMessage(null);
    }
  }, [weekDetails]);

  const changedTopicIds = (weekDetails?.topics || [])
    .filter((topic) => (editedXPostUrls[topic.id!] ?? "") !== (topic.xPostUrl || ""))
    .map((topic) => topic.id!);

  const validationErrors = Object.fromEntries(
    Object.entries(editedXPostUrls)
      .map(([topicId, url]) => [topicId, validateXPostUrl(url)])
      .filter(([, message]) => message !== null)
  ) as Record<string, string>;

  const handleSaveAll = async () => {
    if (!weekDetails || changedTopicIds.length === 0) return;
    setIsSaving(true);
    setSaveResultMessage(null);

    const failedTopicIds: string[] = [];
    for (const topicId of changedTopicIds) {
      try {
        await updateOpinion(topicId, { xPostUrl: editedXPostUrls[topicId] ?? "" });
      } catch (error) {
        Logger.error(`Failed to update xPostUrl for topic ${topicId}:`, error);
        failedTopicIds.push(topicId);
      }
    }

    setIsSaving(false);
    queryClient.invalidateQueries(["xPostUrlWeekDetails", selectedWeek?.weekId]);
    queryClient.invalidateQueries("topics");

    if (failedTopicIds.length === 0) {
      setSaveResultMessage({
        severity: "success",
        text: `${changedTopicIds.length}件のX投稿URLを保存しました`,
      });
    } else {
      setSaveResultMessage({
        severity: "error",
        text: `${failedTopicIds.length}件の保存に失敗しました。再度お試しください`,
      });
    }
  };

  const weeks = weekOptionsData?.weeks || [];

  if (isLoadingWeeks) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (weeksError) {
    return <Alert severity="error">週リストの取得に失敗しました</Alert>;
  }

  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        X投稿URL設定
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        週のお題ごとに、Xへ投稿したツイートのURLをまとめて設定します。設定したURLは、アプリの結果画面に「他の人の意見を見てみる」リンクとして表示されます。
      </Typography>

      <Grid container spacing={3}>
        {/* 週リスト */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              週を選択
            </Typography>
            <List sx={{ maxHeight: 500, overflow: "auto" }}>
              {weeks.map((week) => (
                <ListItem key={week.weekId} disablePadding>
                  <ListItemButton
                    selected={selectedWeek?.weekId === week.weekId}
                    onClick={() => setSelectedWeek(week)}
                    disabled={!week.hasData}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {week.hasData ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <CalendarIcon color="disabled" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={week.label}
                      primaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* URL設定エリア */}
        <Grid size={{ xs: 12, md: 9 }}>
          {!selectedWeek ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <CalendarIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography color="text.secondary">
                左のリストから問題設定済みの週を選択してください
              </Typography>
            </Paper>
          ) : !selectedWeek.hasData ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                この週は問題が未設定です。先に「週間問題設定」で問題を設定してください。
              </Typography>
            </Paper>
          ) : isLoadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailsError ? (
            <Alert severity="error">詳細の取得に失敗しました</Alert>
          ) : weekDetails ? (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">{selectedWeek.label}</Typography>
                <Button
                  variant="contained"
                  startIcon={
                    isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />
                  }
                  onClick={handleSaveAll}
                  disabled={
                    isSaving ||
                    changedTopicIds.length === 0 ||
                    Object.keys(validationErrors).length > 0
                  }
                >
                  {isSaving
                    ? "保存中"
                    : changedTopicIds.length > 0
                      ? `変更を保存（${changedTopicIds.length}件）`
                      : "変更なし"}
                </Button>
              </Box>

              {saveResultMessage && (
                <Alert severity={saveResultMessage.severity} sx={{ mb: 2 }}>
                  {saveResultMessage.text}
                </Alert>
              )}

              {weekDetails.topics.length === 0 ? (
                <Alert severity="info">この週に問題が設定されていません。</Alert>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {weekDetails.topics.map((topic, index) => {
                    const editedUrl = editedXPostUrls[topic.id!] ?? "";
                    const isChanged = editedUrl !== (topic.xPostUrl || "");
                    const validationMessage = validationErrors[topic.id!];
                    return (
                      <Card key={topic.id} variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                            <Chip
                              label={`${index + 1}`}
                              size="small"
                              color="primary"
                              sx={{ minWidth: 28 }}
                            />
                            <DifficultyChip difficulty={topic.difficulty} size="small" />
                            <GenreChip genre={topic.genre} size="small" />
                            <Typography variant="body1" sx={{ ml: 0.5 }}>
                              {topic.theme}
                            </Typography>
                            {isChanged && (
                              <Chip
                                label="未保存"
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          <Box display="flex" alignItems="center" gap={1}>
                            <TextField
                              fullWidth
                              size="small"
                              label="X投稿URL"
                              placeholder="https://x.com/..."
                              value={editedUrl}
                              onChange={(e) =>
                                setEditedXPostUrls((previous) => ({
                                  ...previous,
                                  [topic.id!]: e.target.value.trim(),
                                }))
                              }
                              error={!!validationMessage}
                              helperText={validationMessage}
                            />
                            {editedUrl && !validationMessage && (
                              <Link
                                href={editedUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: "inline-flex", alignItems: "center" }}
                                aria-label="X投稿を開く"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </Link>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Box>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};

export default XPostUrlSettings;
